import type { PreferenciasA11y } from '../../shared/acessibilidade';

/**
 * Cliente HTTP unico. Duas responsabilidades que nao podem ser esquecidas em
 * nenhuma chamada:
 *   * `credentials: 'same-origin'` -- o cookie de sessao e HttpOnly;
 *   * `X-Requested-With: fetch` -- segunda camada de CSRF exigida pelo worker.
 * Por isso nenhuma tela chama `fetch` direto.
 */
export class ErroApi extends Error {
  readonly codigo: string;
  readonly status: number;
  readonly detalhes?: unknown;

  constructor(status: number, codigo: string, mensagem: string, detalhes?: unknown) {
    super(mensagem);
    this.name = 'ErroApi';
    this.status = status;
    this.codigo = codigo;
    this.detalhes = detalhes;
  }
}

async function pedir<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`/api${caminho}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'fetch',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (r.status === 204) return undefined as T;

  const corpo: unknown = await r.json().catch(() => null);
  if (!r.ok) {
    const e = (corpo as { erro?: { codigo?: string; mensagem?: string; detalhes?: unknown } } | null)?.erro;
    throw new ErroApi(
      r.status,
      e?.codigo ?? 'interno',
      e?.mensagem ?? 'Não conseguimos completar essa ação. Tente de novo.',
      e?.detalhes,
    );
  }
  return corpo as T;
}

export const api = {
  get: <T>(caminho: string) => pedir<T>(caminho),
  post: <T>(caminho: string, corpo?: unknown) =>
    pedir<T>(caminho, { method: 'POST', body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
  put: <T>(caminho: string, corpo: unknown) =>
    pedir<T>(caminho, { method: 'PUT', body: JSON.stringify(corpo) }),
  patch: <T>(caminho: string, corpo: unknown) =>
    pedir<T>(caminho, { method: 'PATCH', body: JSON.stringify(corpo) }),
  del: <T>(caminho: string, corpo?: unknown) =>
    pedir<T>(caminho, { method: 'DELETE', body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
};

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  status: string;
  pref_contraste: PreferenciasA11y['contraste'];
  pref_fonte: PreferenciasA11y['fonte'];
  pref_movimento: PreferenciasA11y['movimento'];
  pref_voz?: NonNullable<PreferenciasA11y['voz']>;
  changelog_visto_em: number | null;
  /** Id do arquivo da foto de perfil, servido em `/f/pub/<id>`. */
  avatar_arquivo_id: string | null;
  /** true quando a pessoa também tem perfil de profissional. */
  eh_profissional: boolean;
  /** true quando quem está aqui é o dono do painel, pelo "acessar como". */
  acesso_admin?: boolean;
  /** true quando esta é a conta do app de alguém da equipe: mostra o atalho do painel. */
  eh_admin?: boolean;
  /** 'rascunho' | 'em_analise' | 'verificado' | 'recusado' | 'suspenso', ou null. */
  perfil_status: string | null;
  /** Quando confirmou o e-mail pelo link; null enquanto não confirmou. */
  email_verificado_em: number | null;
  /** true quando pode aparecer na busca e receber (confirmado, ou conta antiga). */
  email_liberado: boolean;
  /** E-mail novo pedido em "trocar e-mail", à espera do clique no link; null sem troca. */
  email_pendente: string | null;
  /** true quando Termos ou Privacidade mudaram depois do último aceite. */
  aceite_pendente?: boolean;
}

export const confirmarEmail = (token: string) =>
  api.post<{ ok: true; trocado: boolean }>('/auth/email/confirmar', { token });

/** Pede a troca: o link vai para o e-mail novo, e a conta só muda ao clicar. */
export const trocarEmail = (email_novo: string, senha: string) =>
  api.post<{ ok: true; email_pendente: string }>('/auth/email/trocar', { email_novo, senha });

export const cancelarTrocaEmail = () => api.post<null>('/auth/email/trocar/cancelar', {});

export const reenviarConfirmacaoEmail = () =>
  api.post<{ ok: true; ja_confirmado: boolean }>('/auth/email/reenviar');

export const euQuery = {
  queryKey: ['eu'] as const,
  queryFn: async () => {
    try {
      return (await api.get<{ usuario: Usuario }>('/auth/eu')).usuario;
    } catch (e) {
      if (e instanceof ErroApi && e.status === 401) return null;
      throw e;
    }
  },
  staleTime: 60_000,
};
